import React from 'react';

import { deriveGasInterconnectDerate } from '../../../../lib/generationImpact/gasInterconnectDerate';
import { GENERATION_EMISSIONS_MODEL_SCOPE_NOTE, waterDutyBasis } from '../../../../lib/generationImpact/reportProjection';
import { validateGenerationLayoutExhibitSnapshot } from '../../../../lib/generationImpact/generationLayoutExhibit';
import { renderGenerationLayoutExhibitSvg } from '../../../../lib/generationImpact/generationLayoutExhibitSvg';
import { computeTakeoffsFromExhibitFit } from '../../../../lib/generationImpact/generationLayoutTakeoffs';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import {
  GENERATION_IMPACT_HASH_VERSION,
  GENERATION_IMPACT_MODEL_VERSION,
  GENERATION_LAYOUT_EXHIBIT_EXPORT_ID,
  GENERATION_LAYOUT_EXHIBIT_MODEL_VERSION,
  GENERATION_LAYOUT_EXHIBIT_SCHEMA_VERSION,
  type EngineeringVerdict,
  type GenerationImpactSnapshot,
  type GenerationLayoutExhibitSnapshot,
  type PublicGenerationLayoutExhibitProjection,
} from '../../../../types/generationImpact';
import type { SiteRagReport } from '../../../../types/siteRag';
import {
  EditorialCallout,
  EditorialDatasheet,
  EditorialExhibit,
  EditorialKpiRail,
  type DatasheetGroup,
  type EditorialKpiTone,
} from '../../report/editorial';
import { SectionShell } from '../SectionShell';

const SECTION_ID = 'generationLayoutExhibit';
const SECTION_TITLE = 'On-Site Generation Test Fit Exhibit';
const NOT_MODELED = 'Not evaluated';

function canonicalGenerationImpact(
  value: SiteRagReport['generationImpactAtGen'],
): GenerationImpactSnapshot | null {
  if (!value || !('noise' in value)) return null;
  const scenario = value.scenario;
  if (
    typeof scenario.id !== 'string'
    || typeof scenario.inputHash !== 'string'
    || typeof scenario.resultHash !== 'string'
    || typeof scenario.result?.modelVersion !== 'string'
  ) return null;
  return value;
}

function finite(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value);
}

function number(value: number | null | undefined, digits = 1): string {
  if (!finite(value)) return NOT_MODELED;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

function signedNumber(value: number | null | undefined, digits = 1): string {
  if (!finite(value)) return NOT_MODELED;
  return `${value > 0 ? '+' : ''}${number(value, digits)}`;
}

function mw(value: number | null | undefined): string {
  const formatted = number(value, 1);
  return formatted === NOT_MODELED ? formatted : `${formatted} MW`;
}

function acres(value: number | null | undefined): string {
  const formatted = number(value, 1);
  return formatted === NOT_MODELED ? formatted : `${formatted} ac`;
}

function metres(value: number | null | undefined): string {
  const formatted = number(value, 1);
  return formatted === NOT_MODELED ? formatted : `${formatted} m`;
}

function dailyVolume(value: number | null | undefined): string {
  if (!finite(value)) return NOT_MODELED;
  if (Math.abs(value) >= 1_000_000) return `${number(value / 1_000_000, 2)} million gal/day`;
  return `${number(value, 0)} gal/day`;
}

function tonnesPerYear(value: number | null | undefined): string {
  const formatted = number(value, 0);
  return formatted === NOT_MODELED ? formatted : `${formatted} t/yr`;
}

function verdictTone(verdict: EngineeringVerdict): EditorialKpiTone {
  if (verdict === 'pass') return 'positive';
  if (verdict === 'fail') return 'negative';
  return 'warning';
}

function fitTone(status: GenerationLayoutExhibitSnapshot['fit']['status']): 'positive' | 'warning' | 'negative' {
  if (status === 'fits') return 'positive';
  if (status === 'partial') return 'negative';
  return 'warning';
}

function fitLabel(status: GenerationLayoutExhibitSnapshot['fit']['status']): string {
  if (status === 'fits') return 'Modeled components fit parcel';
  if (status === 'partial') return 'Partial / outside parcel';
  return 'Indeterminate';
}

function triState(value: boolean | null): string {
  if (value == null) return NOT_MODELED;
  return value ? 'Satisfied' : 'Not satisfied';
}

function uniqueText(values: readonly (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of values) {
    const value = raw?.trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
}

function plainRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const PUBLIC_EXHIBIT_GEOMETRY_KEYS = new Set([
  'anchor',
  'center',
  'components',
  'coordinates',
  'features',
  'fileName',
  'georeference',
  'lat',
  'lng',
  'location',
  'parcelPolygonM',
  'polygon',
  'polygonM',
  'rawStoragePath',
  'sha256',
  'signedUrl',
  'sourceIds',
  'storagePath',
  'xM',
  'yM',
]);

function containsPublicExhibitGeometry(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(containsPublicExhibitGeometry);
  if (!plainRecord(value)) return false;
  return Object.entries(value).some(([key, nested]) => (
    PUBLIC_EXHIBIT_GEOMETRY_KEYS.has(key) || containsPublicExhibitGeometry(nested)
  ));
}

/**
 * Fail closed at the renderer boundary. The share builder creates this field
 * from a sanitized semantic block, but persisted/public payloads still cross
 * an untyped JSON boundary and must not be allowed to smuggle geometry back
 * into the unauthenticated report compositor.
 */
function publicGenerationLayoutExhibit(
  value: unknown,
): PublicGenerationLayoutExhibitProjection | null {
  if (!plainRecord(value) || containsPublicExhibitGeometry(value)) return null;
  const scenario = value.scenario;
  const engineering = value.engineering;
  const fit = value.fit;
  const overlays = value.overlays;
  if (
    value.schemaVersion !== GENERATION_LAYOUT_EXHIBIT_SCHEMA_VERSION
    || value.modelVersion !== GENERATION_LAYOUT_EXHIBIT_MODEL_VERSION
    || typeof value.capturedAt !== 'string'
    || typeof value.contentHash !== 'string'
    || !/^[a-f0-9]{64}$/i.test(value.contentHash)
    || !plainRecord(scenario)
    || typeof scenario.id !== 'string'
    || typeof scenario.name !== 'string'
    || typeof scenario.inputHash !== 'string'
    || typeof scenario.resultHash !== 'string'
    || !/^[a-f0-9]{64}$/i.test(scenario.inputHash)
    || !/^[a-f0-9]{64}$/i.test(scenario.resultHash)
    || scenario.hashVersion !== GENERATION_IMPACT_HASH_VERSION
    || scenario.modelVersion !== GENERATION_IMPACT_MODEL_VERSION
    || !plainRecord(engineering)
    || !plainRecord(engineering.fleet)
    || !plainRecord(engineering.capacity)
    || !plainRecord(engineering.gas)
    || !plainRecord(engineering.water)
    || !plainRecord(engineering.emissions)
    || !plainRecord(engineering.footprint)
    || !plainRecord(engineering.lighting)
    || !Array.isArray(engineering.gaps)
    || !Array.isArray(engineering.warnings)
    || !Array.isArray(engineering.noiseCaveats)
    || !plainRecord(fit)
    || !plainRecord(overlays)
    || !Array.isArray(overlays.cadSources)
    || !Array.isArray(value.warnings)
  ) return null;
  return value as unknown as PublicGenerationLayoutExhibitProjection;
}

function publicLedgerLabel(path: string): string {
  return path
    .split('.')
    .map((part) => part
      .replace(/\[(\d+)\]/g, ' $1')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/^./, (letter) => letter.toUpperCase()))
    .join(' · ');
}

function publicLedgerValue(value: unknown): string {
  if (value == null) return NOT_MODELED;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function publicLedgerRows(value: unknown, path: string): DatasheetGroup['rows'] {
  if (Array.isArray(value)) {
    if (value.length === 0) return [{ key: publicLedgerLabel(path), value: 'None frozen' }];
    if (value.every((entry) => !plainRecord(entry) && !Array.isArray(entry))) {
      return [{
        key: publicLedgerLabel(path),
        value: value.map(publicLedgerValue).join(' · '),
      }];
    }
    return value.flatMap((entry, index) => publicLedgerRows(entry, `${path}[${index + 1}]`));
  }
  if (plainRecord(value)) {
    return Object.entries(value).flatMap(([key, nested]) => (
      publicLedgerRows(nested, path ? `${path}.${key}` : key)
    ));
  }
  return [{ key: publicLedgerLabel(path), value: publicLedgerValue(value) }];
}

function unavailable(
  report: SiteRagReport,
  label: string,
  message: string,
): React.ReactElement {
  return (
    <SectionShell
      report={report}
      sectionId={SECTION_ID}
      label={label}
      hasData={false}
      required
      emptyMessage={message}
    />
  );
}

export interface GenerationLayoutExhibitSectionProps {
  report: SiteRagReport;
  label: string;
}

/**
 * Report-native, immutable On-Site Generation Test Fit exhibit. Geometry,
 * overlays, schedules and provenance come from generation-time snapshots only;
 * the component never reads the live site store and never re-runs a solver.
 */
export const GenerationLayoutExhibitSection: React.FC<GenerationLayoutExhibitSectionProps> = ({
  report,
  label,
}) => {
  const theme = useEffectiveReportTheme();
  const exhibit = report.generationLayoutExhibitAtGen;
  const publicExhibit = publicGenerationLayoutExhibit(
    report.generationLayoutExhibitPublicAtGen,
  );
  const impact = canonicalGenerationImpact(report.generationImpactAtGen);

  if (!exhibit) {
    if (publicExhibit) {
      const { capacity } = publicExhibit.engineering;
      const publicKpis = [
        { key: 'Normal net', value: number(capacity.normalNetMW, 1), unit: 'MW' },
        { key: 'N−1 net', value: number(capacity.nMinusOneNetMW, 1), unit: 'MW', tone: verdictTone(capacity.nMinusOneVerdict) },
        { key: 'N−1 load margin', value: signedNumber(capacity.nMinusOneMarginMW, 1), unit: 'MW', tone: verdictTone(capacity.nMinusOneVerdict) },
        { key: 'N−1 + reserve margin', value: signedNumber(capacity.nMinusOneReserveMarginMW, 1), unit: 'MW', tone: verdictTone(capacity.nMinusOneReserveVerdict) },
        { key: 'Physical footprint', value: number(publicExhibit.engineering.footprint.totalAcres, 1), unit: 'ac', tone: fitTone(publicExhibit.fit.status) },
      ] satisfies Parameters<typeof EditorialKpiRail>[0]['items'];
      const publicGroups: DatasheetGroup[] = [
        {
          heading: 'Frozen identity & scenario',
          meta: 'Coordinate-free public projection',
          rows: publicLedgerRows({
            schemaVersion: publicExhibit.schemaVersion,
            capturedAt: publicExhibit.capturedAt,
            contentHash: publicExhibit.contentHash,
            scenario: publicExhibit.scenario,
          }, ''),
        },
        {
          heading: 'Frozen engineering ledger',
          rows: publicLedgerRows(publicExhibit.engineering, 'engineering'),
        },
        {
          heading: 'Parcel-fit & overlay accountability',
          rows: publicLedgerRows({
            fit: publicExhibit.fit,
            overlays: publicExhibit.overlays,
            warnings: publicExhibit.warnings,
          }, ''),
        },
      ];
      return (
        <SectionShell report={report} sectionId={SECTION_ID} label={label} hasData>
          <div data-testid="generation-layout-exhibit-public-ledger">
            <EditorialKpiRail theme={theme} items={publicKpis} />
            <EditorialCallout theme={theme} tone="caveat" label="Public immutable projection">
              This schedule is projected from the frozen, hash-bound exhibit ledger. Detailed parcel, component and CAD geometry is intentionally withheld from the unauthenticated render payload; obtain the private report revision for the A-01 drawing.
            </EditorialCallout>
            <EditorialDatasheet theme={theme} groups={publicGroups} />
          </div>
        </SectionShell>
      );
    }
    if (report.generationImpactAtGen && !impact) {
      return unavailable(
        report,
        label,
        'This report revision contains a legacy acoustic-only generation snapshot. The parcel fit and selected CAD/site overlays were not frozen. Promote a current Generation & Impact scenario, select the exhibit layers, and regenerate the Test Fit report.',
      );
    }
    if (impact) {
      return unavailable(
        report,
        label,
        'The generation result is frozen, but this revision predates the immutable parcel-fit and CAD/site-overlay exhibit. Select the exhibit layers and regenerate the Test Fit report; live site layers are intentionally not substituted.',
      );
    }
    return unavailable(
      report,
      label,
      'No promoted Generation & Impact scenario or immutable layout exhibit was frozen into this report revision. Promote a saved scenario, select the exhibit layers, and regenerate the Test Fit report.',
    );
  }

  if (!impact) {
    return unavailable(
      report,
      label,
      'The layout exhibit cannot be verified because its canonical Generation & Impact snapshot is missing or uses the retired legacy shape. Regenerate the Test Fit report from a promoted scenario.',
    );
  }

  const validation = validateGenerationLayoutExhibitSnapshot(exhibit, impact);
  if (!validation.valid) {
    return unavailable(
      report,
      label,
      `The frozen layout exhibit failed its immutable revision checks and is withheld to prevent mixed or corrupted engineering results. ${validation.errors[0] ?? 'The exhibit payload is invalid.'} Regenerate the Test Fit report.`,
    );
  }

  let svgMarkup: string;
  try {
    svgMarkup = renderGenerationLayoutExhibitSvg(exhibit);
  } catch {
    return unavailable(
      report,
      label,
      'The frozen generation exhibit could not be rendered from this revision. No live geometry or recalculated layout was substituted; regenerate the Test Fit report.',
    );
  }

  const { engineering, fit, overlays, provenance, scenario } = exhibit;
  const { capacity, emissions, fleet, footprint, gas, lighting, nearestReceptor, water } = engineering;
  const projectDutyBasis = engineering.projectDutyBasis;
  const fitPlan = fit.plan;
  const takeoffs = computeTakeoffsFromExhibitFit(fit);
  const kpis = [
    { key: 'Normal net', value: number(capacity.normalNetMW, 1), unit: 'MW' },
    { key: 'N−1 net', value: number(capacity.nMinusOneNetMW, 1), unit: 'MW', tone: verdictTone(capacity.nMinusOneVerdict) },
    { key: 'N−1 load margin', value: signedNumber(capacity.nMinusOneMarginMW, 1), unit: 'MW', tone: verdictTone(capacity.nMinusOneVerdict) },
    { key: 'N−1 + reserve margin', value: signedNumber(capacity.nMinusOneReserveMarginMW, 1), unit: 'MW', tone: verdictTone(capacity.nMinusOneReserveVerdict) },
    {
      key: projectDutyBasis ? 'Total reservation' : 'Physical footprint',
      value: number(
        projectDutyBasis?.spatialSummary.totalReservedAcres ?? footprint.totalAcres,
        1,
      ),
      unit: 'ac',
      tone: fitTone(fit.status),
    },
  ] satisfies Parameters<typeof EditorialKpiRail>[0]['items'];

  const capacityGroups: DatasheetGroup[] = [
    {
      heading: 'Capacity & reliability',
      meta: scenario.modelVersion,
      rows: [
        { key: 'Scenario', value: scenario.name },
        { key: 'Generation fleet', value: `${fleet.generationBlockCount} block(s) · ${fleet.technologyGroupCount} technology group(s)` },
        { key: 'ISO gross nameplate', value: mw(fleet.isoGrossNameplateMW) },
        { key: 'Site gross output', value: mw(fleet.siteGrossMW) },
        { key: 'Critical facility load', value: mw(capacity.criticalLoadMW) },
        { key: 'Largest contingency', value: `${mw(capacity.largestContingencyMW)} · ${capacity.largestContingencyGroupId ?? NOT_MODELED}` },
        { key: 'N−1 criterion', value: capacity.nMinusOneVerdict.toUpperCase(), tone: capacity.nMinusOneVerdict === 'pass' ? 'positive' : capacity.nMinusOneVerdict === 'fail' ? 'negative' : 'warning' },
        { key: 'Reserve requirement', value: mw(capacity.reserveRequiredMW) },
        { key: 'N−1 + reserve criterion', value: capacity.nMinusOneReserveVerdict.toUpperCase(), tone: capacity.nMinusOneReserveVerdict === 'pass' ? 'positive' : capacity.nMinusOneReserveVerdict === 'fail' ? 'negative' : 'warning' },
      ],
    },
    {
      heading: 'Generation layout fit',
      meta: fitLabel(fit.status),
      rows: [
        { key: 'Selected parcel', value: fit.parcel?.label || fit.parcel?.id || NOT_MODELED },
        { key: 'Gross parcel area', value: acres(fit.parcel?.areaAcres) },
        { key: 'Required envelope', value: fitPlan ? `${metres(fitPlan.requiredEnvelope.widthM)} × ${metres(fitPlan.requiredEnvelope.heightM)} · ${acres(fitPlan.requiredEnvelope.areaAcres)}` : NOT_MODELED },
        ...(fitPlan?.reservationSummary
          ? [
              { key: 'Permanent equipment reservation', value: acres(fitPlan.reservationSummary.permanentEquipmentAcres) },
              { key: 'Route reservation', value: acres(fitPlan.reservationSummary.routeReservationAcres) },
              { key: 'Construction laydown reservation', value: acres(fitPlan.reservationSummary.constructionLaydownAcres) },
              { key: 'Total reserved area', value: acres(fitPlan.reservationSummary.totalReservedAcres) },
            ]
          : []),
        ...(fit.usable
          ? [
              { key: 'No-build exclusions', value: acres(fit.usable.excludedAcres), tone: 'warning' as const },
              { key: 'Usable (screened) parcel area', value: acres(fit.usable.usableAcres) },
              ...(finite(fit.exclusionOverlapAcres)
                ? [{
                    key: 'Components on excluded ground',
                    value: acres(fit.exclusionOverlapAcres),
                    tone: (fit.exclusionOverlapAcres! > 0.01 ? 'negative' : 'positive') as 'negative' | 'positive',
                  }]
                : []),
            ]
          : []),
        { key: 'Contained physical area', value: acres(fit.containedAcres) },
        { key: 'Outside parcel', value: acres(fit.outsideAcres), tone: finite(fit.outsideAcres) && fit.outsideAcres > 0 ? 'negative' : 'positive' },
        { key: 'Containment', value: finite(fit.containmentPct) ? `${number(fit.containmentPct, 1)}%` : NOT_MODELED, tone: fitTone(fit.status) },
        { key: 'Entered parcel-edge setback', value: metres(fit.layout.edgeSetbackM) },
        { key: 'Minimum parcel-edge clearance', value: metres(fit.minimumParcelEdgeClearanceM), tone: fit.parcelEdgeSetbackSatisfied === false ? 'negative' : fit.parcelEdgeSetbackSatisfied === true ? 'positive' : 'warning' },
        { key: 'Parcel-edge setback', value: triState(fit.parcelEdgeSetbackSatisfied), tone: fit.parcelEdgeSetbackSatisfied === false ? 'negative' : fit.parcelEdgeSetbackSatisfied === true ? 'positive' : 'warning' },
        { key: 'Rotation / orientation', value: `${number(fit.layout.rotationDeg, 1)}° · ${fitPlan?.orientation ?? fit.layout.orientation}` },
      ],
    },
    // Derived at render time from the frozen fit — never persisted, so the
    // hash-validated exhibit content is untouched.
    ...(takeoffs.status === 'available' && takeoffs.totals
      ? [{
          heading: 'Screening quantity takeoffs',
          meta: 'derived from frozen fit',
          rows: [
            ...takeoffs.lines.map(line => ({
              key: line.label,
              value: line.unit === 'count'
                ? `${line.quantity}`
                : line.unit === 'm'
                  ? metres(line.quantity)
                  : acres(line.quantity),
            })),
            ...(takeoffs.totals.excludedGroundAcres != null
              ? [{ key: 'Excluded ground (no-build)', value: acres(takeoffs.totals.excludedGroundAcres), tone: 'warning' as const }]
              : []),
          ],
        }]
      : []),
  ];

  const impactGroups: DatasheetGroup[] = [
    {
      heading: 'Fuel, water & emissions',
      meta: `${waterDutyBasis(impact.scenario.input).statement} ${GENERATION_EMISSIONS_MODEL_SCOPE_NOTE}`,
      rows: [
        { key: 'Peak natural gas', value: `${number(gas.peakMmbtuPerHour, 0)} MMBtu/h · ${number(gas.peakDthPerDay, 0)} Dth/day` },
        { key: 'Annual natural gas', value: `${number(gas.annualMmbtu, 0)} MMBtu/yr` },
        { key: 'Generation withdrawal', value: dailyVolume(water.generationWithdrawalGalPerDay) },
        { key: 'Generation consumption', value: dailyVolume(water.generationConsumptionGalPerDay) },
        { key: 'CCS withdrawal', value: dailyVolume(water.ccsWithdrawalGalPerDay) },
        { key: 'CCS consumption', value: dailyVolume(water.ccsConsumptionGalPerDay) },
        { key: 'Modeled component withdrawal subtotal', value: dailyVolume(water.totalWithdrawalGalPerDay) },
        { key: 'Modeled component consumption subtotal', value: dailyVolume(water.totalConsumptionGalPerDay) },
        { key: 'Modeled on-site generation uncontrolled CO₂', value: tonnesPerYear(emissions.uncontrolledCo2TonnesPerYear) },
        { key: 'Modeled on-site generation captured CO₂', value: tonnesPerYear(emissions.capturedCo2TonnesPerYear) },
        { key: 'Modeled on-site generation residual CO₂', value: tonnesPerYear(emissions.residualCo2TonnesPerYear) },
        { key: 'NOx / CO', value: `${tonnesPerYear(emissions.noxTonnesPerYear)} / ${tonnesPerYear(emissions.coTonnesPerYear)}` },
      ],
    },
    {
      heading: 'Neighbour screening',
      meta: 'Free-field planning basis',
      rows: [
        { key: 'Nearest receptor', value: nearestReceptor?.receptorName ?? NOT_MODELED },
        { key: 'Receptor distance', value: metres(nearestReceptor?.distanceM) },
        { key: 'Predicted receptor level', value: finite(nearestReceptor?.predictedDbA) ? `${number(nearestReceptor?.predictedDbA, 1)} dBA` : NOT_MODELED },
        { key: 'Noise threshold', value: finite(nearestReceptor?.thresholdDbA) ? `${number(nearestReceptor?.thresholdDbA, 1)} dBA` : NOT_MODELED },
        { key: 'Noise margin', value: finite(nearestReceptor?.marginDb) ? `${signedNumber(nearestReceptor?.marginDb, 1)} dB` : NOT_MODELED, tone: nearestReceptor?.verdict === 'exceedance' ? 'negative' : nearestReceptor?.verdict === 'pass' ? 'positive' : 'warning' },
        { key: 'Noise verdict', value: nearestReceptor?.verdict.toUpperCase() ?? NOT_MODELED, tone: nearestReceptor?.verdict === 'exceedance' ? 'negative' : nearestReceptor?.verdict === 'pass' ? 'positive' : 'warning' },
        { key: 'Installed site lighting', value: finite(lighting.installedPowerKW) ? `${number(lighting.installedPowerKW, 1)} kW` : NOT_MODELED },
        { key: 'Installed lumens', value: finite(lighting.installedLumens) ? `${number(lighting.installedLumens, 0)} lm` : NOT_MODELED },
        { key: 'Lighting controls / flags', value: lighting.planningFlags.length > 0 ? lighting.planningFlags.join(' · ') : (lighting.installedPowerKW == null && lighting.installedLumens == null ? NOT_MODELED : 'None flagged') },
      ],
    },
  ];

  const equipmentBasis = engineering.equipmentBasis;
  const equipmentGroups: DatasheetGroup[] = equipmentBasis ? [{
    heading: 'Vendor equipment duty',
    meta: `${equipmentBasis.selection.catalogVersion} · ${equipmentBasis.alignmentStatus}`,
    rows: [
      { key: 'Equipment reference', value: `${equipmentBasis.selection.manufacturer} ${equipmentBasis.selection.model} · ${equipmentBasis.selection.trainConfiguration}` },
      { key: 'Published output', value: `${number(equipmentBasis.selection.publishedRating.outputMWPerBlock, 1)} MW · ${equipmentBasis.selection.publishedRating.ratingScope.replaceAll('-', ' ')}` },
      { key: 'Published heat rate / efficiency', value: equipmentBasis.selection.publishedRating.heatRateValue == null
        ? 'Not published in the selected source basis'
        : `${number(equipmentBasis.selection.publishedRating.heatRateValue, 0)} ${equipmentBasis.selection.publishedRating.heatRateUnit ?? ''} ${equipmentBasis.selection.publishedRating.heatingValueBasis ?? ''}${equipmentBasis.selection.publishedRating.efficiencyPct == null ? '' : ` · ${number(equipmentBasis.selection.publishedRating.efficiencyPct, 1)}%`}` },
      { key: 'Published reference conditions', value: equipmentBasis.selection.publishedRating.referenceConditions },
      { key: 'Published physical reference', value: equipmentBasis.selection.publishedPhysicalReference == null
        ? 'No public package dimensions retained for this reference'
        : `${number(equipmentBasis.selection.publishedPhysicalReference.lengthM, 3)} × ${number(equipmentBasis.selection.publishedPhysicalReference.widthM, 3)} × ${number(equipmentBasis.selection.publishedPhysicalReference.heightM, 3)} m${equipmentBasis.selection.publishedPhysicalReference.massTonnes == null ? '' : ` · ${number(equipmentBasis.selection.publishedPhysicalReference.massTonnes, 0)} t dry`} · ${equipmentBasis.selection.publishedPhysicalReference.scope.replaceAll('-', ' ')} only · ${equipmentBasis.selection.publishedPhysicalReference.limitation}` },
      { key: 'Solver ISO gross normalization', value: `${number(equipmentBasis.selection.screeningNormalization.solverIsoGrossMWPerBlock, 2)} MW/block` },
      { key: 'Solver heat-rate basis', value: `${number(equipmentBasis.selection.screeningNormalization.solverHeatRateBtuPerKWhHhv, 0)} Btu/kWh HHV on gross output${equipmentBasis.selection.screeningNormalization.naturalGasLhvToHhvRatio == null ? '' : ` · LHV/HHV ${equipmentBasis.selection.screeningNormalization.naturalGasLhvToHhvRatio.toFixed(2)}`}` },
      { key: 'Duty alignment', value: equipmentBasis.alignmentStatus.toUpperCase(), tone: equipmentBasis.alignmentStatus === 'aligned' ? 'positive' : 'warning' },
      { key: 'Contingency / common-mode boundary', value: equipmentBasis.selection.commonModeBoundary },
      { key: 'Cooling basis', value: `${equipmentBasis.selection.screeningNormalization.coolingSystem.replaceAll('-', ' ')} · ${equipmentBasis.selection.dataStatus.coolingHeatBalance}` },
      { key: 'Ambient curve', value: equipmentBasis.selection.dataStatus.ambientCorrectionCurve },
      { key: 'Fuel-gas pressure', value: equipmentBasis.selection.dataStatus.fuelGasPressure },
      { key: 'Acoustic source data', value: equipmentBasis.selection.dataStatus.acousticSourceData },
      { key: 'Installed footprint', value: equipmentBasis.selection.dataStatus.installedPlantFootprint },
      { key: 'Planning land formula', value: `${number(equipmentBasis.selection.screeningNormalization.plantAcresPerGrossMW, 2)} acres/site-rated gross MW + ${number(equipmentBasis.selection.screeningNormalization.coolingAcres, 1)} cooling acres + ${number(equipmentBasis.selection.screeningNormalization.switchyardAcres, 1)} switchyard acres · not OEM installed dimensions` },
      { key: 'CCS integration', value: equipmentBasis.selection.dataStatus.ccsIntegration },
      { key: 'Reliability / maintenance', value: equipmentBasis.selection.dataStatus.reliabilityAndMaintenance },
      ...equipmentBasis.selection.publishedSources.map((source, index) => ({
        key: `Published source ${index + 1}`,
        value: `${source.publisher} · ${source.title} · ${source.url} · retrieved ${source.retrievedAt}`,
      })),
    ],
  }] : [{
    heading: 'Vendor equipment duty',
    meta: 'Optional study gap',
    rows: [{
      key: 'Equipment basis',
      value: 'Not evaluated — no frozen vendor equipment basis was selected for this scenario.',
      tone: 'warning',
    }],
  }];

  const projectDutyGroups: DatasheetGroup[] = projectDutyBasis ? [
    {
      heading: 'Project OEM / EPC duty authority',
      meta: `${projectDutyBasis.alignmentStatus} · ${projectDutyBasis.completeness.suppliedCount}/${projectDutyBasis.completeness.totalCount} evidence categories supplied`,
      rows: [
        { key: 'Project duty basis', value: projectDutyBasis.basis.basisName },
        // A dossier binds to EITHER an immutable catalog row or a user-supplied
        // entry. Name the one it actually has rather than asserting a catalog id.
        projectDutyBasis.basis.catalogBinding
          ? { key: 'Catalog binding', value: projectDutyBasis.basis.catalogBinding.catalogId }
          : {
              key: 'Equipment binding',
              value: `User-supplied entry · ${projectDutyBasis.basis.userEquipmentBinding?.entryId ?? 'unknown'} (unverified)`,
              tone: 'warning' as const,
            },
        { key: 'Duty alignment', value: projectDutyBasis.alignmentStatus.toUpperCase(), tone: projectDutyBasis.alignmentStatus === 'aligned' ? 'positive' : 'warning' },
        ...Object.entries(projectDutyBasis.completeness.statuses).map(([key, status]) => ({
          key: `Evidence · ${key.replace(/([a-z0-9])([A-Z])/g, '$1 $2')}`,
          value: status.toUpperCase(),
          tone: status === 'supplied' ? 'positive' as const : 'warning' as const,
        })),
      ],
    },
    {
      heading: 'Installed, route & construction reservations',
      meta: 'Exact sourced dimensions where supplied',
      rows: [
        { key: 'Generation block reservation', value: `${projectDutyBasis.basis.installedReservations.blockQuantity} × ${metres(projectDutyBasis.basis.installedReservations.generationBlockEnvelope.lengthM)} × ${metres(projectDutyBasis.basis.installedReservations.generationBlockEnvelope.widthM)}` },
        { key: 'Maintenance clearance included', value: projectDutyBasis.basis.installedReservations.generationBlockEnvelope.maintenanceClearanceIncluded ? 'Yes' : 'No', tone: projectDutyBasis.basis.installedReservations.generationBlockEnvelope.maintenanceClearanceIncluded ? 'positive' : 'warning' },
        { key: 'Fire access included', value: projectDutyBasis.basis.installedReservations.generationBlockEnvelope.fireAccessIncluded ? 'Yes' : 'No', tone: projectDutyBasis.basis.installedReservations.generationBlockEnvelope.fireAccessIncluded ? 'positive' : 'warning' },
        { key: 'Permanent equipment area', value: acres(projectDutyBasis.spatialSummary.permanentEquipmentAcres) },
        { key: 'Natural-gas / CO₂ route area', value: acres(projectDutyBasis.spatialSummary.routeReservationAcres) },
        { key: 'Construction laydown area', value: acres(projectDutyBasis.spatialSummary.constructionLaydownAcres) },
        { key: 'Total reserved area', value: acres(projectDutyBasis.spatialSummary.totalReservedAcres) },
      ],
    },
    {
      heading: 'Project utility & source dossier',
      meta: 'Pressure reference and operating case remain explicit',
      rows: [
        { key: 'Fuel-gas equipment minimum', value: projectDutyBasis.basis.fuelGas
          ? `${number(projectDutyBasis.basis.fuelGas.minimumRequired.valueKPa, 1)} kPa ${projectDutyBasis.basis.fuelGas.minimumRequired.reference}`
          : NOT_MODELED },
        { key: 'Documented site pressure minimum', value: projectDutyBasis.basis.fuelGas?.documentedSiteSupplyMinimum
          ? `${number(projectDutyBasis.basis.fuelGas.documentedSiteSupplyMinimum.valueKPa, 1)} kPa ${projectDutyBasis.basis.fuelGas.documentedSiteSupplyMinimum.reference}`
          : NOT_MODELED },
        { key: 'Fuel-gas pressure window', value: `${projectDutyBasis.gasPressureCheck.verdict.toUpperCase()} · minimum margin ${finite(projectDutyBasis.gasPressureCheck.marginKPa) ? `${signedNumber(projectDutyBasis.gasPressureCheck.marginKPa, 1)} kPa` : NOT_MODELED} · maximum screening headroom ${finite(projectDutyBasis.gasPressureCheck.upperMarginKPa) ? `${signedNumber(projectDutyBasis.gasPressureCheck.upperMarginKPa, 1)} kPa` : NOT_MODELED}`, tone: projectDutyBasis.gasPressureCheck.verdict === 'pass' ? 'positive' : projectDutyBasis.gasPressureCheck.verdict === 'fail' ? 'negative' : 'warning' },
        ...(projectDutyBasis.basis.fuelGas?.supply ? (() => {
          const supply = projectDutyBasis.basis.fuelGas!.supply!;
          const derate = deriveGasInterconnectDerate(
            supply,
            projectDutyBasis.basis.fuelGas!.documentedSiteSupplyMinimum,
          );
          const units = supply.capacityBasis.kind === 'energy' ? 'Dth/day' : 'MMscf/day';
          return [
            { key: 'Gas interconnect rating', value: `${number(supply.stationDesignCapacity, 2)} ${units} at ${number(supply.ratedAtPressure.valueKPa, 1)} kPa ${supply.ratedAtPressure.reference}${supply.contractMaxDailyQuantity == null ? '' : ` · ${number(supply.contractMaxDailyQuantity, 2)} ${units} contract MDQ`}` },
            {
              key: 'Gas interconnect quantity basis',
              value: supply.quantityBasis === 'firm-transport-agreement'
                ? 'Firm transport agreement'
                : 'Indicative · scoping class, not firm transport',
              tone: supply.quantityBasis === 'firm-transport-agreement' ? 'positive' as const : 'warning' as const,
            },
            {
              key: 'Capacity at minimum delivery pressure',
              value: derate.deratedDeliverableDthPerDay == null
                ? `${NOT_MODELED} · transporter minimum delivery pressure is undocumented`
                : `${number(derate.deratedDeliverableDthPerDay, 0)} Dth/day · ${derate.deratedBindingLimit === 'contract-mdq' ? 'contract binds' : 'station binds'}${derate.compressibilityBasis === 'ideal-gas-assumed' ? ` ± ${number(derate.uncertaintyDthPerDay, 0)} (ideal-gas)` : ''}`,
              tone: derate.deratedDeliverableDthPerDay == null ? 'warning' as const : undefined,
            },
          ];
        })() : []),
        { key: 'Generation water authority', value: projectDutyBasis.basis.water
          ? `${projectDutyBasis.basis.water.kind.replaceAll('-', ' ')} · ${projectDutyBasis.basis.water.operatingCase}`
          : NOT_MODELED },
        { key: 'Applied acoustic sources', value: `${projectDutyBasis.basis.acoustics?.acousticSources.length ?? 0}` },
        ...projectDutyBasis.basis.sources.flatMap(source => source.disclosure === 'public'
          ? [{
              key: 'Public project source',
              value: `${source.publisher} · ${source.title} · ${source.url} · retrieved ${source.retrievedAt}`,
            }]
          : []),
        { key: 'Private project sources', value: `${projectDutyBasis.basis.sources.filter(source => source.disclosure === 'private').length} retained in the hash-bound dossier; identity withheld from report rendering` },
      ],
    },
  ] : [{
    heading: 'Project OEM / EPC duty authority',
    meta: 'Optional study gap',
    rows: [{
      key: 'Project-duty basis',
      value: 'Not evaluated — no frozen project OEM / EPC duty dossier was applied.',
      tone: 'warning',
    }],
  }];

  const sizingStudy = engineering.sizingStudy;
  const sizingGroups: DatasheetGroup[] = [{
    heading: 'N−1 capacity alternatives',
    meta: sizingStudy?.modelVersion ?? 'Legacy exhibit revision',
    rows: sizingStudy?.status === 'available'
      ? sizingStudy.options.map(option => ({
          key: `${option.label}${option.id === sizingStudy.lowestNameplatePassingReserveOptionId ? ' · lowest nameplate screen' : ''}`,
          value: `${option.blockCount} × ${number(option.isoGrossMWPerBlock, 0)} MW ISO · N−1 ${number(option.nMinusOneNetMW, 1)} MW · reserve ${signedNumber(option.nMinusOneReserveMarginMW, 1)} MW · independent load-served ${finite(option.probabilityLoadServed) ? `${number(option.probabilityLoadServed * 100, 3)}%` : NOT_MODELED} · ${number(option.peakGasDthPerDay, 0)} Dth/day · ${dailyVolume(option.generationAndCcsConsumptionGalPerDay)} · ${acres(option.footprintAcres)}`,
          tone: option.nMinusOneReserveVerdict === 'pass'
            ? 'positive' as const
            : option.nMinusOneVerdict === 'pass'
              ? 'warning' as const
              : 'negative' as const,
        }))
      : [{
          key: 'Sizing study',
          value: `Not evaluated — ${sizingStudy?.reason ?? 'the sizing alternatives study was not frozen in this report revision.'}`,
          tone: 'warning' as const,
        }],
  }];

  const granularity = sizingStudy?.contingencyGranularity;
  const granularityGroups: DatasheetGroup[] = [{
    heading: 'N−1 contingency granularity',
    meta: granularity
      ? `${number(granularity.basisRequirementMW, 0)} MW ${granularity.basis === 'reserve' ? 'reserve-padded' : 'critical-load'} duty · ${granularity.incrementMW} MW procurement increment`
      : 'Legacy exhibit revision',
    rows: granularity
      ? granularity.rows.map(row => ({
          key: `${row.blockCount} × ${number(row.requiredGrossMWPerBlock, 1)} MW ISO${row.blockCount === granularity.configuredBlockCount ? ' · configured basis' : ''}`,
          value: `${number(row.requiredInstalledGrossMW, 0)} MW installed · ${number(row.requiredInstalledRelativeToFinest * 100, 0)}% of the finest granularity screened · N−1 firm ${number(row.firmAfterContingencyMW, 1)} MW · rounded to ${number(row.installedGrossMW, 0)} MW at ${granularity.incrementMW} MW steps${row.incrementRoundingPenaltyMW > 0 ? ` (+${number(row.incrementRoundingPenaltyMW, 0)} MW round-up)` : ''}`,
          tone: row.blockCount === granularity.configuredBlockCount ? 'positive' as const : undefined,
        }))
      : [{
          key: 'Contingency granularity',
          value: 'Not evaluated — the granularity curve was not frozen in this report revision.',
          tone: 'warning' as const,
        }],
  }];

  const cadRows = overlays.cadSources.length > 0
    ? overlays.cadSources.map((source, index) => ({
        key: `CAD source ${index + 1}`,
        value: `${source.sourceFile} · ${source.featureCount} feature(s) · SHA-256 ${source.sourceSha256}`,
    }))
    : [{ key: 'CAD sources', value: 'No DWG-derived overlay source frozen' }];
  const frozenAuthority = provenance.frozenAuthority;
  const provenanceGroups: DatasheetGroup[] = [
    {
      heading: 'Frozen exhibit provenance',
      rows: [
        { key: 'Scenario identity', value: `${scenario.name} · ${scenario.id}` },
        { key: 'Scenario input SHA-256', value: scenario.inputHash },
        { key: 'Scenario result SHA-256', value: scenario.resultHash },
        { key: 'Scenario hash convention', value: scenario.hashVersion },
        { key: 'Exhibit projection model', value: exhibit.modelVersion },
        { key: 'Exhibit SHA-256', value: exhibit.contentHash },
        { key: 'Captured at', value: exhibit.capturedAt },
        { key: 'Site-layer revision', value: provenance.siteLayersRevision == null ? NOT_MODELED : `${provenance.siteLayersRevision}` },
        { key: 'Canonical layer version', value: provenance.siteLayersCanonicalVersion == null ? NOT_MODELED : `${provenance.siteLayersCanonicalVersion}` },
        { key: 'Frozen visible layers', value: `${provenance.visibleLayerCount} of ${provenance.sourceLayerCount}` },
        { key: 'Included overlay layers', value: `${overlays.includedLayerIds.length}` },
        { key: 'Overlay vertices', value: number(overlays.totalVertices, 0) },
        { key: 'Selected parcel SHA-256', value: provenance.selectedParcelHash ?? NOT_MODELED },
        ...(frozenAuthority
          ? [
              {
                key: 'Published layout authority',
                value: `${frozenAuthority.layoutPublicationId} · v${frozenAuthority.layoutVersion} · SHA-256 ${frozenAuthority.layoutHash}`,
              },
              {
                key: 'Parcel boundary authority',
                value: `${acres(frozenAuthority.boundaryAreaAcres)} · SHA-256 ${frozenAuthority.boundaryHash}`,
              },
              {
                key: 'Site Evidence Document authority',
                value: frozenAuthority.sedId && frozenAuthority.sedVersion != null
                  ? `${frozenAuthority.sedId} · v${frozenAuthority.sedVersion}`
                  : 'Not evaluated — legacy exhibit has no compact SED identity.',
                tone: frozenAuthority.sedId && frozenAuthority.sedVersion != null
                  ? 'positive' as const
                  : 'warning' as const,
              },
            ]
          : []),
        ...cadRows,
      ],
    },
  ];

  const caveats = uniqueText([
    'This exhibit checks the frozen modeled components. A complete DC + BTM campus fit still requires data halls, shared infrastructure, access and drainage, and permanent and temporary needs by phase; gross acreage or component containment alone does not establish campus fit.',
    fit.error,
    !nearestReceptor
      ? 'Noise receptor study: Not evaluated — no frozen receptor result is available.'
      : null,
    lighting.installedPowerKW == null && lighting.installedLumens == null
      ? 'Lighting study: Not evaluated — no installed-lighting power or photometric result is frozen.'
      : null,
    !equipmentBasis
      ? 'Vendor equipment duty: Not evaluated — scenario planning assumptions remain the basis.'
      : null,
    !projectDutyBasis
      ? 'Project OEM / EPC duty: Not evaluated — no project dossier is frozen.'
      : null,
    ...fit.caveats,
    ...engineering.noiseCaveats,
    ...engineering.gaps,
    ...engineering.warnings,
    ...(sizingStudy?.warnings ?? []),
    ...(equipmentBasis?.alignmentNotes ?? []),
    equipmentBasis?.selection.screeningNormalization.normalizationNote,
    ...(equipmentBasis?.selection.unavailableFields ?? []),
    ...(projectDutyBasis?.alignmentNotes ?? []),
    ...(projectDutyBasis?.completeness.missingItems ?? []),
    projectDutyBasis?.gasPressureCheck.caveat,
    projectDutyBasis?.basis.performance?.normalizationNote,
    ...exhibit.warnings,
  ]);
  const invalidLayerPreview = overlays.invalidLayerIds.slice(0, 5).join(', ');
  const invalidLayerOverflow = Math.max(0, overlays.invalidLayerIds.length - 5);

  return (
    <SectionShell report={report} sectionId={SECTION_ID} label={label} hasData>
      <EditorialKpiRail theme={theme} items={kpis} />

      <EditorialExhibit
        theme={theme}
        span="columns"
        frame
        captionNumber={1}
        captionPrefix="Exhibit"
        caption="Frozen parcel-fit geometry and selected CAD/site context for the promoted generation scenario. Geometry is screening-grade and must be replaced by vendor and civil design before permitting or construction."
      >
        <div
          data-testid={GENERATION_LAYOUT_EXHIBIT_EXPORT_ID}
          data-export-capture=""
          data-export-id={GENERATION_LAYOUT_EXHIBIT_EXPORT_ID}
          data-export-title={SECTION_TITLE}
          data-section-id={SECTION_ID}
          role="img"
          aria-label={`${SECTION_TITLE} for ${scenario.name}`}
          className="[&>svg]:block [&>svg]:h-auto [&>svg]:w-full"
          style={{
            width: '100%',
            overflow: 'hidden',
            background: theme.backgroundColor,
          }}
          dangerouslySetInnerHTML={{ __html: svgMarkup }}
        />
      </EditorialExhibit>

      {overlays.status === 'omitted-too-large' && (
        <EditorialCallout theme={theme} tone="warning" label="Overlay accountability">
          The frozen CAD/site overlay exceeded the {number(overlays.maxBytes, 0)}-byte exhibit ceiling, so all {overlays.omittedLayerIds.length} visible layers were omitted as one atomic set. No partial or live-layer substitute is shown. Reduce the selected overlay set and regenerate the Test Fit report.
        </EditorialCallout>
      )}
      {overlays.status === 'omitted-invalid' && (
        <EditorialCallout theme={theme} tone="negative" label="Invalid overlay geometry">
          The frozen layer set contained invalid geometry in {overlays.invalidLayerIds.length} layer(s) ({invalidLayerPreview}{invalidLayerOverflow > 0 ? `, +${invalidLayerOverflow} more` : ''}), so all {overlays.omittedLayerIds.length} visible overlays were omitted atomically. Correct the listed site/CAD geometry and regenerate the Test Fit report; no valid-looking prefix or live-layer substitute is shown.
        </EditorialCallout>
      )}

      <EditorialDatasheet theme={theme} groups={capacityGroups} />
      {equipmentGroups.length > 0 && <EditorialDatasheet theme={theme} groups={equipmentGroups} />}
      {projectDutyGroups.length > 0 && <EditorialDatasheet theme={theme} groups={projectDutyGroups} />}
      <EditorialDatasheet theme={theme} groups={sizingGroups} />
      <EditorialDatasheet theme={theme} groups={granularityGroups} />
      <EditorialDatasheet theme={theme} groups={impactGroups} />
      <EditorialDatasheet theme={theme} groups={provenanceGroups} />

      {caveats.length > 0 && (
        <EditorialCallout theme={theme} tone="caveat" label="Screening basis & open items">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {caveats.map((caveat) => <li key={caveat}>{caveat}</li>)}
          </ul>
        </EditorialCallout>
      )}
    </SectionShell>
  );
};
